import { isBefore, startOfDay } from 'date-fns';
import { TimelineSegment, DueDateMarker } from '@/components/atoms';
import { TIMELINE_LAYOUT, SEGMENT_INFO } from '@/constants/timeline';
import { isZeroDuration } from '@/hooks/useTimelineData';
import type { DeliverableTimelineData, ColorScheme, SegmentType, TooltipSegment } from '@/types/timeline';

export interface TimelineRowProps {
  /** Timeline data for this row */
  data: DeliverableTimelineData;
  /** Y position of the row */
  y: number;
  /** Total width of the SVG (chart area only, not including labels) */
  totalWidth: number;
  /** Row color (team color) */
  color: string;
  /** Active color scheme for segments */
  activeColors: ColorScheme;
  /** Convert date to X position */
  dateToX: (date: Date | null) => number;
  /** Callback when segment is hovered */
  onSegmentHover?: (segment: TooltipSegment | null, element: SVGElement | null) => void;
  /** Timeline start date - due dates before this are not rendered (shown in label column instead) */
  timelineStartDate?: Date;
  /** Callback when row is clicked to show/hide dependency connections */
  onRowConnectionClick?: (deliverableId: string | null) => void;
  /** Whether dependency connections are being shown for this row */
  isConnectionTarget?: boolean;
  /** Whether this row should be dimmed (not in the active dependency path) */
  isDimmed?: boolean;
  /** Path highlight color (when this row is in the dependency path) */
  pathHighlightColor?: string | null;
  /** Whether this is the SELECTED deliverable (the one that was clicked to show dependencies) */
  isSelected?: boolean;
}

/**
 * Timeline row molecule - renders timeline bars for a deliverable
 *
 * Composes TimelineSegment atoms for each probability range and DueDateMarker.
 * Labels are rendered separately in TimelineLabelColumn for sticky positioning.
 *
 * Note: Bars start at x=0 since this component renders in the chart area only.
 * The label column is handled by TimelineLabelColumn.
 */
export function TimelineRow({
  data,
  y,
  totalWidth,
  color,
  activeColors,
  dateToX,
  onSegmentHover,
  timelineStartDate,
  onRowConnectionClick,
  isConnectionTarget,
  isDimmed,
  pathHighlightColor,
  isSelected,
}: TimelineRowProps) {
  const barY = y + (TIMELINE_LAYOUT.ROW_HEIGHT - TIMELINE_LAYOUT.BAR_HEIGHT) / 2;

  // Has dependencies?
  const hasDependencies = data.predecessorIds.length > 0 || data.dependentIds.length > 0;

  // Click handler for showing/hiding dependency connections
  const handleRowClick = () => {
    if (onRowConnectionClick && data.deliverableId && hasDependencies) {
      onRowConnectionClick(data.deliverableId);
    }
  };

  // Create hover handler factory
  const createHoverHandler = (segmentType: SegmentType, startDate: Date, endDate: Date) => {
    return (event: React.MouseEvent<SVGElement>) => {
      onSegmentHover?.(
        {
          type: segmentType,
          label: SEGMENT_INFO[segmentType].label,
          probability: SEGMENT_INFO[segmentType].probability,
          startDate,
          endDate,
          deliverableName: data.deliverableName,
          teamName: data.teamName,
          featureName: data.featureName,
        },
        event.currentTarget,
      );
    };
  };

  const handleMouseLeave = () => {
    onSegmentHover?.(null, null);
  };

  // Calculate opacity based on state
  // Connection target: highlighted (0.25), Dimmed: faded (0.03), Normal: default (0.1)
  const backgroundOpacity = isConnectionTarget ? 0.25 : isDimmed ? 0.03 : 0.1;
  // Dimmed rows have reduced overall opacity for their bars
  const contentOpacity = isDimmed ? 0.25 : 1;
  // Use path highlight color for connection targets, otherwise use team color
  const rowBackgroundColor = isConnectionTarget && pathHighlightColor ? pathHighlightColor : color;

  return (
    <g
      onClick={handleRowClick}
      className={`
        ${isConnectionTarget ? 'dependency-connection-target' : ''}
        ${hasDependencies && onRowConnectionClick ? `cursor-pointer` : ''}
      `}
      style={{ opacity: contentOpacity }}
    >
      {/* Row background */}
      <rect
        x={0}
        y={y}
        width={totalWidth}
        height={TIMELINE_LAYOUT.ROW_HEIGHT}
        fill={rowBackgroundColor}
        opacity={backgroundOpacity}
        className="transition-opacity duration-200"
      />

      {/* Selection border - makes the clicked deliverable visually distinct */}
      {isSelected && pathHighlightColor && (
        <rect
          x={1}
          y={y + 1}
          width={totalWidth - 2}
          height={TIMELINE_LAYOUT.ROW_HEIGHT - 2}
          fill="none"
          stroke={pathHighlightColor}
          strokeWidth={2}
          rx={3}
          className="pointer-events-none"
        />
      )}

      {/* Timeline bars - start at x=0 since labels are in separate HTML column */}
      {isZeroDuration(data) ? (
        /* Zero-duration deliverable: render a diamond marker at P50 date
         * instead of full probability bars. These represent gating mechanisms
         * (approvals, handoffs) that complete instantly. */
        data.startP50 && (
          <g
            className="cursor-pointer"
            onMouseEnter={(e) => createHoverHandler('activeWork', data.startP50!, data.startP50!)(e)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Diamond marker at P50 position */}
            <polygon
              points={`${dateToX(data.startP50)},${barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2 - 8} ${dateToX(data.startP50) + 8},${barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2} ${dateToX(data.startP50)},${barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2 + 8} ${dateToX(data.startP50) - 8},${barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2}`}
              fill={activeColors.activeWork}
              opacity={0.7}
            />
            {/* Horizontal whisker line showing the range (P20 → P95) */}
            {data.startP20 && data.finishP95 && (
              <line
                x1={dateToX(data.startP20)}
                y1={barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2}
                x2={dateToX(data.finishP95)}
                y2={barY + TIMELINE_LAYOUT.BAR_HEIGHT / 2}
                stroke={activeColors.activeWork}
                strokeWidth={2}
                strokeOpacity={0.4}
                strokeDasharray="4 3"
              />
            )}
          </g>
        )
      ) : (
        <>
          {/* Maybe started: P20 - P50 */}
          {data.startP20 && data.startP50 && (
            <TimelineSegment
              x={dateToX(data.startP20)}
              y={barY}
              width={dateToX(data.startP50) - dateToX(data.startP20)}
              height={TIMELINE_LAYOUT.BAR_HEIGHT}
              fill={activeColors.maybeStarted}
              segmentType="maybeStarted"
              isFirst={true}
              radius={TIMELINE_LAYOUT.BAR_RADIUS}
              onMouseEnter={(e) => createHoverHandler('maybeStarted', data.startP20!, data.startP50!)(e)}
              onMouseLeave={handleMouseLeave}
            />
          )}

          {/* Active work: P50 start - P50 finish */}
          {data.startP50 && data.finishP50 && (
            <TimelineSegment
              x={dateToX(data.startP50)}
              y={barY}
              width={dateToX(data.finishP50) - dateToX(data.startP50)}
              height={TIMELINE_LAYOUT.BAR_HEIGHT}
              fill={activeColors.activeWork}
              segmentType="activeWork"
              onMouseEnter={(e) => createHoverHandler('activeWork', data.startP50!, data.finishP50!)(e)}
              onMouseLeave={handleMouseLeave}
            />
          )}

          {/* Maybe completion: P50 - P70 */}
          {data.finishP50 && data.finishP70 && (
            <TimelineSegment
              x={dateToX(data.finishP50)}
              y={barY}
              width={dateToX(data.finishP70) - dateToX(data.finishP50)}
              height={TIMELINE_LAYOUT.BAR_HEIGHT}
              fill={activeColors.maybeCompletion}
              segmentType="maybeCompletion"
              onMouseEnter={(e) => createHoverHandler('maybeCompletion', data.finishP50!, data.finishP70!)(e)}
              onMouseLeave={handleMouseLeave}
            />
          )}

          {/* Probable completion: P70 - P85 */}
          {data.finishP70 && data.finishP85 && (
            <TimelineSegment
              x={dateToX(data.finishP70)}
              y={barY}
              width={dateToX(data.finishP85) - dateToX(data.finishP70)}
              height={TIMELINE_LAYOUT.BAR_HEIGHT}
              fill={activeColors.probableCompletion}
              segmentType="probableCompletion"
              onMouseEnter={(e) => createHoverHandler('probableCompletion', data.finishP70!, data.finishP85!)(e)}
              onMouseLeave={handleMouseLeave}
            />
          )}

          {/* High probable: P85 - P95 */}
          {data.finishP85 && data.finishP95 && (
            <TimelineSegment
              x={dateToX(data.finishP85)}
              y={barY}
              width={dateToX(data.finishP95) - dateToX(data.finishP85)}
              height={TIMELINE_LAYOUT.BAR_HEIGHT}
              fill={activeColors.highProbable}
              segmentType="highProbable"
              isLast={true}
              radius={TIMELINE_LAYOUT.BAR_RADIUS}
              onMouseEnter={(e) => createHoverHandler('highProbable', data.finishP85!, data.finishP95!)(e)}
              onMouseLeave={handleMouseLeave}
            />
          )}
        </>
      )}

      {/* Due date marker - only render if due date is on or after timeline start */}
      {/* Due dates before timeline start are shown as warning icons in the label column instead */}
      {data.dueDate && (!timelineStartDate || !isBefore(startOfDay(data.dueDate), startOfDay(timelineStartDate))) && (
        <DueDateMarker
          x={dateToX(data.dueDate)}
          barY={barY}
          barHeight={TIMELINE_LAYOUT.BAR_HEIGHT}
          dueDate={data.dueDate}
          probability={data.dueDateProbability}
          activeColors={activeColors}
        />
      )}
    </g>
  );
}
